import * as React from 'react';
import {connect} from 'react-redux';

import _ from 'underscore';

import {formEncodedApi} from 'nutshell-core/api';
import * as Schema from 'nutshell-core/schema';
import {Session} from 'nutshell-core/session';
import type {FilterObject, NotifyMessage, EntityListType, User} from 'nutshell-core/types';

import type {NutshellState} from '../../../store';

import {FullPageEntityListContainer} from '../../core/entity-list/full-page/full-page-entity-list-container';
import {calculateMainContentHeight} from '../../core/entity-list/layout/calculate-main-content-height';
import {FilterDrawerEntityListWrapper} from '../../core/entity-list/layout/filter-drawer-entity-list-wrapper';
import {FilterDrawerMainContentWrapper} from '../../core/entity-list/layout/filter-drawer-main-content-wrapper';
import type {SequenceTemplate} from '../../entity-list/email/bulk-email/bulk-email-modals/bulk-personal-email-modal';
import {SendFromListModal} from '../../entity-list/email/send-from-list-modal';
import type {ReportURLParams} from '../../reports/report-types';

import {CompanyListFooter} from './company-list-footer';
import {EmailMarketingFilterDrawer} from './email-marketing-filter-drawer';
import {EmailMarketingListControls} from './email-marketing-list-controls';
import {sendBulkEmail, getBulkEditFilterParams, exportSelectedContacts} from './helpers';
import {ListProviderContext} from './list-provider-context';
import * as actions from './list-selection-redux/list-selection-actions';
import * as selectors from './list-selection-redux/list-selection-selectors';
import {PeopleListFooter} from './people-list-footer';

type OwnProps = {
    audienceId?: string;
    audienceName?: string;
    isWebFxAudience?: boolean;
    // Redux connect requires exact prop types, so we have to include these prop
    // definations even though they're all unused.

    parentEntityId?: string;

    campaignType?: string;

    editionId?: string;

    dripSequenceId?: string;

    formId?: string;
    unfilteredCount: number;
    unfilteredCountThreshold: number;
    defaultFields: string[];
    filters: FilterObject[];
    search: string;
    selectedPage: number;
    selectedSortField: string;
    selectedFields: string[];
    urlParams: ReportURLParams<any>;
    onParamChange: (arg1: any, arg2: boolean) => any;
    onRemoveAllFilters: () => void;
    showEmailButton?: boolean;
    fieldFilterFunction: (fieldName: string) => boolean;
    customEmptyStateComponent: React.ReactNode;
    entityType: EntityListType;
    hideFooterExport?: boolean;
    // i.e. '#audience-content-scroll-container'
    scrollContainerSelector: string;
    graduatedFilters?: React.ReactNode;
    hasPeopleListFooter?: boolean;
    hideSearch?: boolean;

    hideSelectionCheckboxes?: boolean;
    handleRowClick?: (arg1: string) => void;
    // visitors company list footer props
    hasUnlockCompaniesButton?: boolean;
};

type ContextProps = {
    schema: Schema.Properties;
    count: number;
    isFirstLoad: boolean;
    isLoading: boolean;
    hasError: boolean;
    listItems: any[];
    isFilterDrawerOpen: boolean;
    fetchList: (arg1: FilterObject[], arg2: number, arg3: string[], sort: string, q: string) => any;
    fetchContactsSchema?: () => void;
    fetchAccountsSchema?: () => void;
    fetchMcfxCompaniesSchema?: () => void;
    toggleShowFilterDrawer: () => void;
    onClickEmailButtonForSingleEntity: (arg1: string) => void;
};

type StateProps = {
    selectedItems: string[];
    selectionIsExcluded: boolean;
    validNotifies: NotifyMessage[];
    user: User | null | undefined;
};

type DispatchProps = {
    toggleItem: (arg1: string) => void;
    toggleSelectAll: () => void;
    resetSelection: () => void;
    shiftSelectListRowItem: (arg1: string, arg2: any[]) => void;
    resetLastSelectedItem: () => void;
    onAcknowledgeExportDownload: () => void;
    pollForQueuedExports: () => void;
};

type ConnectedProps = OwnProps & StateProps & DispatchProps;
type Props = ConnectedProps & ContextProps;
type State = {
    scrollToColumnIndex: number;
    isEmailModalOpen: boolean;
    query: {
        filter: {
            selected: FilterObject[];
        };
        search: {
            selected: string;
        };
    };
    omittedItems: string[];
    massSelect: boolean;
};

function mapStateToProps(state: NutshellState): StateProps {
    return {
        selectedItems: selectors.getAudienceListSelectedItems(state),
        selectionIsExcluded: selectors.getAudienceListSelectionIsExcluded(state),
        validNotifies: Session.getSessionStateUserUnacknowedgedExportNotifications(state),
        user: Session.getSessionStateUser(state),
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        toggleItem: (entityId: string) => {
            dispatch(actions.selectListRowItem({entityId}));
        },
        toggleSelectAll: () => {
            dispatch(actions.selectAllRows());
        },
        resetSelection: () => {
            dispatch(actions.resetSelection());
        },
        shiftSelectListRowItem: (entityId, listItems) => {
            dispatch(actions.shiftSelectListRowItem({entityId, listItems}));
        },
        resetLastSelectedItem: () => {
            dispatch(actions.resetLastSelectedItem());
        },
        onAcknowledgeExportDownload: () => {
            dispatch(Session.requestFetchSession());
        },
        pollForQueuedExports: () => {
            dispatch(Session.pollForQueuedExports());
        },
    };
}

class EntityListContainerComponent extends React.Component<Props, State> {
    // @ts-expect-error
    resizeObserver: ResizeObserver;
    pageContentDiv: HTMLElement | null | undefined;
    pageContentTop: number;
    listControls: HTMLElement | null | undefined;
    listFooter: HTMLElement | null | undefined;
    pageFooter: HTMLElement | null | undefined;
    footerHeight: number;

    constructor() {
        // @ts-expect-error
        super();

        this.pageContentTop = 0;
        this.footerHeight = 0;

        this.state = {
            scrollToColumnIndex: 0,
            isEmailModalOpen: false,
            query: {
                filter: {
                    selected: [{}],
                },
                search: {
                    selected: '',
                },
            },
            omittedItems: [],
            massSelect: false,
        };
    }

    componentDidMount() {
        this.props.fetchList(
            this.props.filters,
            this.props.selectedPage,
            this.props.selectedFields,
            this.props.selectedSortField,
            this.props.search
        );

        this.props.resetSelection();

        // @ts-expect-error
        this.pageContentDiv = document.querySelector(this.props.scrollContainerSelector);

        this.resizeObserver = new ResizeObserver((entries) => {
            let shouldUpdate = false;
            for (const entry of entries) {
                if (entry.target === this.pageContentDiv) {
                    // Detect and react to changes in the offset of the scroll container
                    // (this happens during FOUC in development, for example)
                    const pageOffsetTop =
                        // @ts-expect-error
                        entry.target.offsetParent &&
                        // @ts-expect-error
                        entry.target.offsetParent instanceof HTMLElement &&
                        // @ts-expect-error
                        entry.target.offsetParent.offsetTop;
                    if (
                        typeof pageOffsetTop === 'number' &&
                        pageOffsetTop !== this.pageContentTop
                    ) {
                        this.pageContentTop = pageOffsetTop;
                    }

                    shouldUpdate = true;
                } else if (entry.target === this.listFooter) {
                    const footer = entry.target;
                    if (footer.clientHeight !== this.footerHeight) {
                        this.footerHeight = footer.clientHeight;
                        shouldUpdate = true;
                    }
                }
            }

            // If something that affects layout has changed, we need to re-render
            if (shouldUpdate) {
                this.forceUpdate();
            }
        });

        if (this.pageContentDiv) {
            this.resizeObserver.observe(this.pageContentDiv);
        }
    }

    componentDidUpdate(prevProps: Props) {
        const prevSelectedCount = this.props.selectionIsExcluded
            ? this.props.count - prevProps.selectedItems.length
            : prevProps.selectedItems.length;
        const selectedCount = this.props.selectionIsExcluded
            ? this.props.count - this.props.selectedItems.length
            : this.props.selectedItems.length;

        // Handle footer appearing/disappearing due to selection
        if (
            prevSelectedCount !== selectedCount ||
            this.props.selectionIsExcluded !== prevProps.selectionIsExcluded
        ) {
            if (selectedCount > 0 && this.listFooter) {
                this.resizeObserver.observe(this.listFooter);
            } else if (selectedCount === 0) {
                if (this.listFooter) {
                    this.resizeObserver.unobserve(this.listFooter);
                }
                this.footerHeight = 0;
                this.forceUpdate();
            }
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const filtersChanged =
            JSON.stringify(nextProps.filters) !== JSON.stringify(this.props.filters);
        const pageWasChanged = nextProps.selectedPage !== this.props.selectedPage;
        const sortWasChanged = nextProps.selectedSortField !== this.props.selectedSortField;

        // Need to sort the new and old columns so that changes in order do _not_
        // trigger another (unnecessary) fetch.
        const oldFieldsSorted = _.sortBy(this.props.selectedFields, (fieldName) => fieldName);
        const newFieldsSorted = _.sortBy(nextProps.selectedFields, (fieldName) => fieldName);
        const fieldsChanged = JSON.stringify(newFieldsSorted) !== JSON.stringify(oldFieldsSorted);
        const searchChanged = nextProps.search !== this.props.search;

        if (filtersChanged || fieldsChanged || pageWasChanged || sortWasChanged || searchChanged) {
            this.props.fetchList(
                nextProps.filters,
                nextProps.selectedPage,
                nextProps.selectedFields,
                nextProps.selectedSortField,
                nextProps.search
            );
        }

        if (filtersChanged || searchChanged || sortWasChanged) {
            this.props.resetSelection();
        }

        if (pageWasChanged) {
            this.props.resetLastSelectedItem();
        }
    }

    UNSAFE_componentWillMount() {
        document.addEventListener('bulkEditDidApply', this.onEdit, false);
        if (this.props.fetchContactsSchema) {
            this.props.fetchContactsSchema();
        }

        if (this.props.fetchAccountsSchema) {
            this.props.fetchAccountsSchema();
        }

        if (this.props.fetchMcfxCompaniesSchema) {
            this.props.fetchMcfxCompaniesSchema();
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('bulkEditDidApply', this.onEdit);

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    render() {
        const mainContentHeight = calculateMainContentHeight({
            pageContentHeight: this.pageContentDiv ? this.pageContentDiv.offsetHeight : 0,
            headerHeight: this.listControls ? this.listControls.offsetHeight : 0,
            footerHeight: this.footerHeight,
            headerOffset: 0,
            hasNavbarOffset: false,
            hasTrialStatusBarOffset: false,
        });

        const selectedCount = this.props.selectionIsExcluded
            ? this.props.count - this.props.selectedItems.length
            : this.props.selectedItems.length;

        return (
            <div>
                <EmailMarketingListControls
                    // Need to pass in the base filters here, not the filtered ones.
                    filters={this.props.filters}
                    fieldFilterFunction={this.props.fieldFilterFunction}
                    isRefreshingFilters={this.props.isLoading}
                    isFilterDrawerOpen={this.props.isFilterDrawerOpen}
                    entityCount={this.props.count}
                    onParamChange={this.props.onParamChange}
                    removeAllFilters={this.props.onRemoveAllFilters}
                    currentSearch={this.props.search}
                    onToggleFilterDrawerShown={this.props.toggleShowFilterDrawer}
                    graduatedFilters={this.props.graduatedFilters}
                    getRef={(node) => {
                        this.listControls = node;
                    }}
                    hideSearch={this.props.hideSearch}
                    entityType={this.props.entityType}
                />
                <FilterDrawerEntityListWrapper mainContentHeight={mainContentHeight}>
                    <EmailMarketingFilterDrawer
                        urlParams={this.props.urlParams}
                        entityType={this.props.entityType}
                        fieldFilterFunction={this.props.fieldFilterFunction}
                        schema={this.props.schema}
                        visibleActiveFilters={this.getApplicableFilters()}
                        activeFilters={this.props.filters}
                        isOpen={this.props.isFilterDrawerOpen}
                        onParamChange={this.props.onParamChange}
                        shownColumns={this.props.selectedFields}
                        handleScrollToColumn={(isAddingColumn) => {
                            this.setState({scrollToColumnIndex: isAddingColumn ? -1 : 0});
                        }}
                    />
                    <FilterDrawerMainContentWrapper
                        isFilterDrawerOpen={this.props.isFilterDrawerOpen}
                    >
                        <FullPageEntityListContainer
                            isFirstLoad={this.props.isFirstLoad}
                            isLoading={this.props.isLoading}
                            hasError={this.props.hasError}
                            filters={this.getApplicableFilters()}
                            defaultFields={this.props.defaultFields}
                            selectedPage={this.props.selectedPage}
                            selectedSortField={this.props.selectedSortField}
                            selectedFields={this.props.selectedFields}
                            onParamChange={this.props.onParamChange}
                            unfilteredCount={this.props.unfilteredCount}
                            unfilteredCountThreshold={this.props.unfilteredCountThreshold}
                            onRemoveAllFilters={this.props.onRemoveAllFilters}
                            schema={this.props.schema}
                            listItems={this.props.listItems}
                            count={this.props.count}
                            entityType={this.props.entityType}
                            includeColumnPicker={this.props.entityType !== 'mcfxcompanies'}
                            isFilterDrawerOpen={this.props.isFilterDrawerOpen}
                            scrollContainerSelector={this.props.scrollContainerSelector}
                            footerHeight={this.listFooter ? this.listFooter.offsetHeight : 0}
                            customEmptyStateComponent={this.props.customEmptyStateComponent}
                            toggleItem={this.props.toggleItem}
                            handleRowClick={this.props.handleRowClick}
                            shiftSelectItem={(entityId: string) => {
                                this.props.shiftSelectListRowItem(entityId, this.props.listItems);
                            }}
                            checkRowSelected={(entityId: string) => {
                                if (this.props.selectionIsExcluded) {
                                    return !this.props.selectedItems.includes(entityId);
                                }

                                return this.props.selectedItems.includes(entityId);
                            }}
                            selectAllItems={this.props.toggleSelectAll}
                            isAllSelected={
                                (this.props.selectionIsExcluded &&
                                    this.props.selectedItems.length === 0) ||
                                // If the user has manually selected all
                                // items in the list
                                (!this.props.selectionIsExcluded &&
                                    this.props.selectedItems.length === this.props.count)
                            }
                            scrollToColumnIndex={this.state.scrollToColumnIndex}
                        />
                    </FilterDrawerMainContentWrapper>
                </FilterDrawerEntityListWrapper>
                {this.props.hasPeopleListFooter ? (
                    <PeopleListFooter
                        audienceName={this.props.audienceName}
                        isWebFxAudience={this.props.isWebFxAudience}
                        selectedItemCount={selectedCount}
                        onRemove={this.props.audienceId ? this.onRemoveFromAudience : undefined}
                        onEmailClick={
                            this.props.showEmailButton ? () => this.handleEmailClick() : undefined
                        }
                        onExport={this.handleExportSelectedContacts}
                        onEdit={this.onOpenBulkEditModal}
                        getRef={(node) => {
                            this.listFooter = node;
                        }}
                        onAddToAudience={this.handleAddToAudience}
                        onAcknowledgeExportDownload={this.props.onAcknowledgeExportDownload}
                        validNotifies={this.props.validNotifies}
                        hideExport={this.props.hideFooterExport}
                    />
                ) : undefined}
                {this.props.hasUnlockCompaniesButton ? (
                    <CompanyListFooter
                        selectedCompanies={
                            this.props.selectionIsExcluded
                                ? this.props.listItems.filter(
                                      (item) => !this.props.selectedItems.includes(item.id)
                                  )
                                : this.props.listItems.filter((item) =>
                                      this.props.selectedItems.includes(item.id)
                                  )
                        }
                        resetSelection={this.props.resetSelection}
                        getRef={(node) => {
                            this.listFooter = node;
                        }}
                    />
                ) : undefined}
                {this.state.isEmailModalOpen &&
                this.props.user &&
                this.props.entityType !== 'mcfxcompanies' ? (
                    <SendFromListModal
                        onClose={() => this.setState({isEmailModalOpen: false})}
                        selectedCount={selectedCount}
                        canAccessMarketing={true}
                        userEmail={
                            this.props.user.sendForUser && this.props.user.sendForUser.length
                                ? // @ts-expect-error
                                  this.props.user.sendForUser[0].email
                                : undefined
                        }
                        handleOpenPersonalEmailComposer={() => {
                            this.props.onClickEmailButtonForSingleEntity(
                                // @ts-expect-error
                                this.props.selectedItems[0]
                            );
                        }}
                        // Props for the bulk personal email modal
                        userId={this.props.user.id}
                        entityType={this.props.entityType}
                        query={this.state.query}
                        massSelect={this.state.massSelect}
                        selectedItems={this.props.selectedItems}
                        omittedItems={this.state.omittedItems}
                        onEmailSend={this.handleSendBulkEmail}
                        // Props for the marketing outreach email modal
                        numRecipients={selectedCount}
                        canAccessFullCampaigns={this.props.user.permissions.canAccessFullCampaigns}
                    />
                ) : undefined}
            </div>
        );
    }

    getApplicableFilters = () => {
        // @ts-expect-error
        const filterObjFilter = (filterObj) =>
            // @ts-expect-error
            this.props.fieldFilterFunction(Object.keys(filterObj)[0]);

        return this.props.filters.filter(filterObjFilter);
    };

    handleEmailClick = () => {
        const filterParams = this.handleGetBulkEditFilterParams();
        const keys = filterParams.filter ? Object.keys(filterParams.filter) : [];
        const selected = keys.map((key) => {
            const selection: Record<string, any> = {};

            selection[key] = filterParams.filter[key];

            return selection;
        });

        const query = {
            filter: {
                selected,
            },
            search: {
                selected: filterParams.q,
            },
        } as const;

        const omittedItems = filterParams.omittedItems;
        const massSelect = filterParams.massSelect;

        this.setState({isEmailModalOpen: true, query, omittedItems, massSelect});
    };

    handleSendBulkEmail = (
        template: SequenceTemplate,
        options: {
            includeIncomplete: boolean;
            includeRecentlyReceivedTemplate: boolean;
        }
    ) => {
        sendBulkEmail(
            template,
            options,
            this.props.selectedItems,
            this.props.selectionIsExcluded,
            this.props.entityType,
            this.state.query,
            this.props.resetSelection
        );
    };

    onOpenBulkEditModal = () => {
        Modal.open({
            url: '/bulk-edit-person',
            urlData: {...this.handleGetBulkEditFilterParams(), newFilters: true},
            classNames: 'ui-modal__bulk-edit',
        });
    };

    onEdit = () => {
        this.props.resetSelection();
    };

    handleExportSelectedContacts = () => {
        exportSelectedContacts(
            this.handleGetBulkEditFilterParams(),
            this.props.selectedFields,
            this.props.resetSelection,
            this.props.pollForQueuedExports
        );
    };

    onRemoveFromAudience = () => {
        const audienceId = this.props.audienceId;

        if (!audienceId) {
            return;
        }

        const defaultParams = {
            newFilters: true,
            removeAudienceIds: [audienceId],
        } as const;

        this.bulkEdit(defaultParams)
            .catch(() => {
                throw new Error('Something went wrong. Not everyone was remove from the audience');
            })
            .then(() => {
                this.props.resetSelection();
                this.props.fetchList(
                    this.props.filters,
                    this.props.selectedPage,
                    this.props.selectedFields,
                    this.props.selectedSortField,
                    this.props.search
                );
            });
    };

    handleAddToAudience = (
        audienceId: string,
        onlyAddPrimaryContactToAudience: boolean
    ): Promise<any> => {
        const defaultParams = {
            newFilters: true,
            audienceId,
            onlyAddPrimaryContactToAudience,
        } as const;

        return this.bulkEdit(defaultParams)
            .then(() => {
                this.props.resetSelection();
            })
            .catch(() => {
                throw new Error('Something went wrong. Not everyone was added to the audience');
            });
    };

    bulkEdit = (params: any): Promise<any> => {
        const postData = {
            ...this.handleGetBulkEditFilterParams(),
            ...params,
        } as const;

        return formEncodedApi.post('/bulk-edit-person/apply.json', postData);
    };

    handleGetBulkEditFilterParams = (): any => {
        return getBulkEditFilterParams(
            this.props.selectionIsExcluded,
            this.props.selectedItems,
            this.props.filters,
            this.props.search
        );
    };
}

function EntityListContainerConnected(props: ConnectedProps) {
    return (
        <ListProviderContext.Consumer>
            {({
                // @ts-expect-error
                isLoading,
                // @ts-expect-error
                isFirstLoad,
                // @ts-expect-error
                hasError,
                // @ts-expect-error
                listItems,
                // @ts-expect-error
                count,
                // @ts-expect-error
                schema,
                // @ts-expect-error
                isFilterDrawerOpen,
                // @ts-expect-error
                fetchContactsSchema,
                // @ts-expect-error
                fetchAccountsSchema,
                // @ts-expect-error
                fetchMcfxCompaniesSchema,
                // @ts-expect-error
                fetchList,
                // @ts-expect-error
                toggleShowFilterDrawer,
                // @ts-expect-error
                onClickEmailButtonForSingleEntity,
            }) => {
                return (
                    <EntityListContainerComponent
                        {...props}
                        // @ts-expect-error
                        toggleItem={props.hideSelectionCheckboxes ? undefined : props.toggleItem}
                        // @ts-expect-error
                        toggleSelectAll={
                            props.hideSelectionCheckboxes ? undefined : props.toggleSelectAll
                        }
                        isLoading={isLoading}
                        isFirstLoad={isFirstLoad}
                        hasError={hasError}
                        listItems={listItems}
                        count={count}
                        schema={schema}
                        fetchContactsSchema={fetchContactsSchema}
                        fetchAccountsSchema={fetchAccountsSchema}
                        fetchMcfxCompaniesSchema={fetchMcfxCompaniesSchema}
                        fetchList={fetchList}
                        toggleShowFilterDrawer={toggleShowFilterDrawer}
                        isFilterDrawerOpen={isFilterDrawerOpen}
                        onClickEmailButtonForSingleEntity={onClickEmailButtonForSingleEntity}
                    />
                );
            }}
        </ListProviderContext.Consumer>
    );
}

// @ts-expect-error
const connector = connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps
);
export const EntityListContainer = connector(EntityListContainerConnected);
